// @flow
import React, { useEffect } from 'react';
import {
  Stack,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Modal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import { Form } from '@fortress-technology-solutions/fortress-component-library/Organisms';
import { FloppyIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';

import messages from './messages';
import { useFloorPlanOptions, useChangeQualifyingFloorPlan } from './hooks';
import useIntl from '../../../hooks/useIntl';
import useReactHookFormProps from '../../../hooks/useReactHookFormProps';
import type { FloorPlan } from '../../ManageFloorPlans/types';

export const SELECT_FLOOR_PLAN_MODAL = 'selectFloorPlanModal';

type Props = {
  open: boolean,
  onClose: Function,
  onSaved: Function,
  organizationId: string,
  propertyId: string,
  applicationId: string,
  floorPlans: ?(FloorPlan[]),
  nBedsArr: ?Array<string | number>,
  storedFloorPlanId: ?string,
};

const SelectFloorPlanModal = ({
  open,
  onClose,
  onSaved,
  organizationId,
  propertyId,
  applicationId,
  floorPlans,
  nBedsArr,
  storedFloorPlanId,
}: Props) => {
  const intl = useIntl();
  const options = useFloorPlanOptions({
    floorPlans,
    nBedsArr,
    storedFloorPlanId,
  });

  const { mutate, isLoading } = useChangeQualifyingFloorPlan({
    organizationId,
    propertyId,
    applicationId,
    onSaved: () => {
      onClose();
      onSaved?.();
    },
  });

  const ReactHookFormProps = useReactHookFormProps({
    defaultValues: { floorPlanId: storedFloorPlanId ?? null },
    mode: 'onChange',
  });
  const { getValues, reset, watch } = ReactHookFormProps;
  // One required field, so the value is the validity. react-hook-form's isValid stays false until
  // a validation pass runs, which never happens when the field opens already holding the stored
  // plan -- that would leave Save disabled on a form the agent has nothing left to fill in.
  const selectedFloorPlanId = watch('floorPlanId');

  // The container mounts this modal permanently and toggles `open`, so the field has to be
  // re-seeded from the current stored plan each time it opens rather than once at mount.
  useEffect(() => {
    if (open) {
      reset({ floorPlanId: storedFloorPlanId ?? null });
    }
  }, [open, storedFloorPlanId, reset]);

  const fields = [
    {
      variant: 'singleSelect',
      name: 'floorPlanId',
      label: intl.formatMessage(messages.floorPlan),
      options,
      rules: { required: true },
      GridProps: { xs: 12 },
    },
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={intl.formatMessage(messages.title)}
      actionsProps={[
        {
          children: intl.formatMessage(messages.cancel),
          onClick: onClose,
          variant: 'text',
        },
        {
          children: intl.formatMessage(messages.save),
          submitButton: true,
          startIcon: <FloppyIcon />,
          disabled: !selectedFloorPlanId || isLoading,
          isSubmitting: isLoading,
          onClick: () => mutate(getValues().floorPlanId),
        },
      ]}
    >
      <Stack spacing={2}>
        <Typography variant={'bodyMedium'}>
          {intl.formatMessage(messages.instruction)}
        </Typography>
        <Typography variant={'bodyMedium'}>
          {intl.formatMessage(messages.warning)}
        </Typography>
        <Form ReactHookFormProps={ReactHookFormProps} fields={fields} />
      </Stack>
    </Modal>
  );
};

export default SelectFloorPlanModal;
